import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeletedEntity } from '@app/_interfaces/feature.interface';
import { createEntry } from '@app/_models/entry.model';
import { List } from '@app/_interfaces/list.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { UtilsService } from '@app/_services/utils.service';
import { ListStore, ViewSettingsStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { ListItemsComponent } from '../list-items/list-items.component';
import { ListBlockComponent } from '../list-block/list-block.component';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-list-trash-tab',
  imports: [
    CommonModule,
    SvgComponent,
    ListBlockComponent
  ],
  templateUrl: './list-trash-tab.component.html',
  styleUrl: './list-trash-tab.component.scss'
})

export class ListTrashTabComponent {
  readonly listStore = inject(ListStore);
  // Show ALL metadata in trash screen (description is controlled by descriptionType, not show array)
  show: string[] = ['label', 'itemCount', 'collaboratorsCount', 'access', 'hashtag', 'deletedAt'];
  viewSettingsStore = inject(ViewSettingsStore)

  deletedLists: Signal<DeletedEntity[]> = computed(() => {
    const deletedEntities: DeletedEntity[] = [];
    this.listStore.lists().forEach(list => {
      if (list.deletedAt && !list.permaDeletedAt && list.uid === this.cc.user.uid) {
        deletedEntities.push({
          entityName: 'list',
          entityId: list.id,
          deletedAt: this.utils.toDate(list.deletedAt),
          entries: [],
          entityEntry: createEntry({
            setupId: list.id,
            entityType: 'list',
            date: '',
            occurDate: '',
            sortTitle: list.title,
            isScheduled: false,
            sortCreatedAt: list.createdAt ?? null,
            tagIds: list.hashtags || [],
          }),
          isRepeated: false
        });
      }
    });

    deletedEntities.sort((a, b) => {
      return b.deletedAt.getTime() - a.deletedAt.getTime();
    });
    return deletedEntities;
  });
  idToList: Signal<Record<string, List>> = this.listStore.idToList;

  constructor(
    public ss: SettingsService,
    private alertService: AlertService,
    public dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private utils: UtilsService,
    private modalService: ModalService
  ) { }

  trackByEntity(index: number, entity: DeletedEntity) {
    return entity.entityId;
  };

  list(entityId: string) {
    return this.idToList()[entityId];
  };

  openListItems(entityId: string) {
    this.modalService.openLevel1(
      ListItemsComponent,
      { value: this.list(entityId) },
      { disableClose: false, autoFocus: false }
    );
  };

  async remove(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const list = this.list(entityId);
    list.permaDeletedAt = new Date();
    this.listStore.updateLists([list]);
  };

  async restore(entityId: string) {

    const isListDisabled = this.viewSettingsStore.showListFeature();
    if (!isListDisabled) {
      await this.alertService.alert(this.ss.t('featureTurnedOff_title'), this.ss.t('featureTurnedOff_content'), this.ss.t('common_ok'));
    }

    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const list = this.list(entityId);
    list.deletedAt = null;
    this.listStore.updateLists([list]);
    const isShow = await this.alertService.toast('todoRestored_content', isListDisabled ? 'common_show' : '', 'common_dismiss');
    if (isShow) {
      this.openListItems(entityId);
    }
  };
}
